import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import SEO from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Messaggio inviato!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore invio. Riprova.");
    } finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-14" data-testid="contact-page">
      <SEO
        title="Contatti — HostMarket"
        description="Contatta HostMarket per informazioni sulla piattaforma, abbonamenti o collaborazioni. Un prodotto di Vita da Host, Genova."
        path="/contatti"
      />
      <header className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[#E619A8] mb-2">· Contatti</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-tight">Parliamo di <span className="text-[#FFBE00]">affitti brevi</span>.</h1>
        <p className="mt-4 text-zinc-400 max-w-2xl leading-relaxed">Scrivici per informazioni sulla piattaforma, abbonamenti, collaborazioni o partnership. Ti rispondiamo entro 24 ore lavorative.</p>
      </header>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
        {/* FORM */}
        <div>
          {sent ? (
            <div className="hm-card p-10 text-center" data-testid="contact-sent">
              <i className="fas fa-envelope-circle-check text-5xl text-[#22C55E] mb-4"></i>
              <h2 className="font-heading font-black text-2xl mb-3">Messaggio ricevuto</h2>
              <p className="text-zinc-400 max-w-sm mx-auto">Ti risponderemo il prima possibile all'indirizzo email che hai indicato. Nel frattempo hai anche ricevuto una copia di conferma.</p>
              <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }} className="mt-6 px-5 py-2 rounded-md border border-zinc-700 text-xs uppercase tracking-wider hover:border-[#FFBE00] hover:text-[#FFBE00]">Invia un altro</button>
            </div>
          ) : (
            <form onSubmit={submit} className="hm-card p-6 space-y-4" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nome / Azienda *">
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name" />
                </Field>
                <Field label="Email *">
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email" />
                </Field>
                <Field label="Telefono (opzionale)">
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="contact-phone" />
                </Field>
                <Field label="Oggetto *">
                  <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="contact-subject" />
                </Field>
              </div>
              <Field label="Messaggio *">
                <textarea required rows={7} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="contact-message" />
              </Field>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-xs text-zinc-500 max-w-sm">Inviando accetti di essere contattato dal team HostMarket via email. Nessun dato viene condiviso con terze parti.</p>
                <button type="submit" disabled={sending} className="px-6 py-3 rounded-md bg-[#E619A8] text-white font-bold uppercase tracking-wider text-sm hover:bg-[#FFBE00] hover:text-black transition-all disabled:opacity-50" data-testid="contact-submit">
                  {sending ? "Invio…" : "Invia messaggio"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* SIDE INFO */}
        <aside className="space-y-4" data-testid="contact-info">
          <div className="hm-card p-6">
            <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-3">· Azienda</div>
            <div className="font-heading font-bold text-2xl">Vita da Host</div>
            <div className="text-sm text-zinc-400 mt-1"><i className="fas fa-map-marker-alt mr-2 text-[#E619A8]"></i> Genova, Italia</div>
            <div className="text-xs text-zinc-500 mt-1">P.IVA 02903680995</div>
            <a href="https://vitadahost.it" target="_blank" rel="noreferrer" className="inline-block mt-4 text-xs uppercase tracking-wider text-[#FFBE00] hover:text-[#E619A8]">
              vitadahost.it <i className="fas fa-arrow-up-right-from-square ml-1 text-[10px]"></i>
            </a>
          </div>

          <div className="hm-card p-6">
            <div className="text-xs uppercase tracking-widest text-[#E619A8] mb-3">· Come scriverci</div>
            <p className="text-sm text-zinc-300 leading-relaxed">
              Per qualunque richiesta usa il <strong className="text-white">form qui a fianco</strong>.
              Ti rispondiamo entro <strong className="text-[#FFBE00]">24 ore lavorative</strong> dall'indirizzo
              email associato al tuo account.
            </p>
            <p className="text-xs text-zinc-500 mt-3">
              Per evitare spam e proteggere la privacy del team, le email dirette non sono pubbliche.
            </p>
          </div>

          <div className="hm-card p-6">
            <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-3">· Per cosa scrivere</div>
            <ul className="space-y-2 text-sm text-zinc-300">
              <li><i className="fas fa-check text-[#22C55E] mr-2"></i>Informazioni su abbonamenti e fatturazione</li>
              <li><i className="fas fa-check text-[#22C55E] mr-2"></i>Supporto tecnico su annunci o account</li>
              <li><i className="fas fa-check text-[#22C55E] mr-2"></i>Partnership e collaborazioni editoriali</li>
              <li><i className="fas fa-check text-[#22C55E] mr-2"></i>Rivendicare un annuncio esistente</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:border-[#E619A8] focus:ring-1 focus:ring-[#E619A8] outline-none";
const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</label>
    {children}
  </div>
);
